import { createAction } from '@reduxjs/toolkit'
import { all, call, ForkEffect, put, select, takeEvery } from 'redux-saga/effects'

import { FLAG_TYPE_IDS, getFlagTypeById } from '../utils/getFlagTypeById'
import { updateFlag } from '../../../api/projects-api'
import { AzureFeatureFlag } from '../../../models/azureFeatureFlags'
import { FlagAxiosAPIResponse, FlagFilters, NormalizedFlag } from '../../../models/flags'
import { Project } from '../../../models/project'
import { AzureFeatureFlagIds } from '../../../shared/constants/azure-feature-flags'
import { isAzureFeatureFlagEnabled } from '../../../shared/services/azure-feature-flag-services/azure-feature-flag-services'
import { isFlagVisibleBasedOnFilters } from '../../../shared/services/flags/flags-service'
import { filterNullValuesFromObject } from '../../../utils/general'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Flag, Workspace } from '../../lib/toolBoxes/2D'
import addTooltipsToFlag from '../../lib/utils/functionality-bindings/addTooltipsToFlag'
import { selectAzureFeatureFlags } from '../../slices/flags'
import { selectFlagById, selectFlagFilters, updateFlagStatus } from '../../slices/materialFlags'
import { selectProject } from '../../slices/project'

interface UpdateFlagData {
    flag_id: number
    flagFormData: {
        status_id?: number
        intake_method_id?: number
        category_id?: number
        error_id?: number
        severity_id?: number
        requirement_source_id?: number | null
        sub_category_id?: number
        description?: string
        submitter_name?: string
    }
}

export const updateFlagAction = createAction<UpdateFlagData>('updateFlag')

export function* handleUpdateFlag({ payload }: ReturnType<typeof updateFlagAction>) {
    try {
        const { flag_id, flagFormData } = payload
        const flag: NormalizedFlag | null = yield select(selectFlagById, flag_id)

        const manager: PaperManager = yield call(managers.get2DManager)

        if (!manager || !flag) return

        const azureFeatureFlags: AzureFeatureFlag[] | null = yield select(selectAzureFeatureFlags)
        const isQRMCommentsEnabled: boolean = isAzureFeatureFlagEnabled(
            azureFeatureFlags,
            AzureFeatureFlagIds.qrm_comments_attachments_mvp
        )

        const workspaceTool: Workspace = yield call(manager.getTool, Workspace.NAME)
        const flagTool: Flag = yield call(manager.getTool, Flag.NAME)

        const project: Project = yield select(selectProject)
        const flagFilters: FlagFilters = yield select(selectFlagFilters)

        const response: FlagAxiosAPIResponse = yield call(updateFlag, project.id, flag_id, {
            ...filterNullValuesFromObject(flagFormData),
            error_id: isQRMCommentsEnabled ? flagFormData.error_id : null,
        })

        const flagItemsByOpeningId: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data) => data?.flag_id === response.updatedFlag.id && data.toolName === Flag.NAME
        )

        yield all(flagItemsByOpeningId.map((item) => call(item.remove.bind(item))))

        // set flag type as task if null
        const type = getFlagTypeById(response.updatedFlag.type_id ?? FLAG_TYPE_IDS.TASK)

        const flags = yield all(
            response.updatedFlag.opening_links.map((link) =>
                call(
                    flagTool.drawFlagByCoords,
                    link.coordinates,
                    response.updatedFlag.id,
                    link.opening_id,
                    flag?.order,
                    type,
                    isFlagVisibleBasedOnFilters(
                        flagFilters,
                        type,
                        response.updatedFlag.status_id,
                        flag.creationJiraStatus?.id
                    ),
                    response.updatedFlag.status_id,
                    flag.creationJiraStatus?.id || null,
                    false,
                    link.id
                )
            )
        )

        yield all(flags.map((flag) => call(addTooltipsToFlag, flag, workspaceTool)))

        yield put(updateFlagStatus(response))
    } catch (error) {
        yield call(console.error, error)
    }
}

export function* watchForUpdateFlag(): Generator<ForkEffect<never>, void, unknown> {
    yield takeEvery(updateFlagAction.type, handleUpdateFlag)
}
